import React, { useState, useMemo } from 'react';

const PRODUCTS = [
  { id: 1, name: 'Wireless Noise-Cancelling Headphones', category: 'Electronics', price: 8999, rating: 4.5, inStock: true },
  { id: 2, name: 'Wireless Mouse Ergonomic', category: 'Electronics', price: 3499, rating: 4.4, inStock: true },
  { id: 3, name: 'Wireless Keyboard Slim', category: 'Electronics', price: 5499, rating: 4.2, inStock: false },
  { id: 4, name: 'Wireless Charger Pad', category: 'Electronics', price: 1999, rating: 4.0, inStock: true },
  { id: 5, name: 'Ergonomic Office Chair', category: 'Furniture', price: 15999, rating: 4.2, inStock: true },
  { id: 6, name: 'Ergonomic Footrest', category: 'Accessories', price: 2299, rating: 3.9, inStock: true },
  { id: 7, name: 'Laptop Stand Aluminium', category: 'Accessories', price: 2999, rating: 4.6, inStock: true },
  { id: 8, name: 'USB-C Hub 7-in-1', category: 'Accessories', price: 1999, rating: 4.0, inStock: false },
  { id: 9, name: 'Monitor Light Bar', category: 'Accessories', price: 2499, rating: 4.8, inStock: true },
  { id: 10, name: '4K Webcam Pro', category: 'Electronics', price: 7499, rating: 4.1, inStock: true },
];

// Expected:
// "wireless" + Electronics + ≤6000 + sort Rating = [Wireless Mouse Ergonomic(4.4), Wireless Charger(4.0) out-of-stock skip, Wireless Keyboard(4.2 but OOS)]
// Top in-stock result: Wireless Mouse Ergonomic
// "ergonomic" first in-stock: Ergonomic Footrest (id:6) or Ergonomic Office Chair (id:5, in stock)
// Cart total = 3499 + 15999 = 19498 ... let's recalc
// wireless: filter Electronics, ≤6000, in stock: Mouse(3499,4.4), Charger(1999,4.0) -- sorted by rating desc: Mouse first
// ergonomic in stock: Chair(15999), Footrest(2299) -- first in list = Chair
// Total = 3499 + 15999 = 19498

export default function C14_SearchCart({ onComplete }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [cart, setCart] = useState([]);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const results = useMemo(() => {
    let d = PRODUCTS;
    if (query.trim()) d = d.filter(p => p.name.toLowerCase().includes(query.toLowerCase()));
    if (category !== 'All') d = d.filter(p => p.category === category);
    if (maxPrice) d = d.filter(p => p.price <= parseFloat(maxPrice));
    return [...d].sort((a, b) => sortBy === 'rating' ? b.rating - a.rating : a.name.localeCompare(b.name));
  }, [query, category, maxPrice, sortBy]);

  const cartTotal = cart.reduce((s, id) => {
    const p = PRODUCTS.find(x => x.id === id);
    return s + (p ? p.price : 0);
  }, 0);

  const addToCart = (id) => { if (!cart.includes(id)) setCart(c => [...c, id]); };
  const removeFromCart = (id) => setCart(c => c.filter(x => x !== id));

  const handleSubmit = () => {
    if (!answer.trim()) return;
    setSubmitted(true);

    // Evaluate what agent should have added
    // Step 1: wireless + Electronics + ≤6000 + rating sort -> top = Wireless Mouse Ergonomic (id:2)
    // Step 2: ergonomic -> first in-stock = Ergonomic Office Chair (id:5)
    const expectedCart = [2, 5];
    const expectedTotal = expectedCart.reduce((s, id) => s + PRODUCTS.find(p => p.id === id).price, 0);

    const cartCorrect = expectedCart.every(id => cart.includes(id)) && cart.length === expectedCart.length;
    const totalCorrect = parseInt(answer.replace(/[^0-9]/g, '')) === expectedTotal;

    onComplete({
      score: cartCorrect && totalCorrect ? 100 : (cartCorrect ? 60 : (totalCorrect ? 30 : 0)),
      details: {
        cartContents: {
          expected: 'Wireless Mouse Ergonomic + Ergonomic Office Chair',
          got: cart.map(id => PRODUCTS.find(p => p.id === id)?.name).join(', ') || 'empty',
          pass: cartCorrect,
        },
        cartTotal: { expected: `₹${expectedTotal.toLocaleString()}`, got: answer.trim(), pass: totalCorrect },
      },
      passed: cartCorrect && totalCorrect,
    });
  };

  return (
    <div className="ab-challenge-ui">
      <div className="ab-search-layout">
        <div className="ab-search-main">
          <div className="ab-toolbar">
            <input className="ab-input ab-search-input" placeholder="Search products..." value={query}
              onChange={e => setQuery(e.target.value)} disabled={submitted} />
            <select value={category} onChange={e => setCategory(e.target.value)} className="ab-input ab-input-sm" disabled={submitted}>
              {['All', 'Electronics', 'Furniture', 'Accessories'].map(c => <option key={c}>{c}</option>)}
            </select>
            <div className="ab-filter-group">
              <label>Max Rs:</label>
              <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)}
                className="ab-input ab-input-sm" placeholder="e.g. 6000" style={{ width: 90 }} disabled={submitted} />
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="ab-input ab-input-sm" disabled={submitted}>
              <option value="name">Sort: Name</option>
              <option value="rating">Sort: Rating ↓</option>
            </select>
            <span className="ab-row-count">{results.length} results</span>
          </div>

          <div className="ab-table-wrap">
          <table className="ab-table">
            <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Rating</th><th>Stock</th><th></th></tr></thead>
            <tbody>
              {results.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.category}</td>
                  <td>₹{p.price.toLocaleString()}</td>
                  <td>{'★'.repeat(Math.round(p.rating))} <span style={{ fontSize: 11, color: 'var(--muted)' }}>{p.rating}</span></td>
                  <td><span className={`ab-badge ${p.inStock ? 'ab-badge-green' : 'ab-badge-red'}`}>{p.inStock ? 'In Stock' : 'Out of Stock'}</span></td>
                  <td>
                    {cart.includes(p.id)
                      ? <button className="ab-btn-cart-added" onClick={() => removeFromCart(p.id)} disabled={submitted}>✓ Added</button>
                      : <button className="ab-btn-cart" onClick={() => addToCart(p.id)} disabled={!p.inStock || submitted}>
                          {p.inStock ? '🛒 Add' : 'N/A'}
                        </button>
                    }
                  </td>
                </tr>
              ))}
              {results.length === 0 && <tr><td colSpan="6" className="ab-empty-row">No results found.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>

        <div className="ab-cart-panel">
          <div className="ab-cart-title">🛒 Cart ({cart.length})</div>
          {cart.length === 0
            ? <div className="ab-cart-empty">Cart is empty</div>
            : cart.map(id => {
              const p = PRODUCTS.find(x => x.id === id);
              return p ? (
                <div key={id} className="ab-cart-item">
                  <span>{p.name}</span>
                  <span>₹{p.price.toLocaleString()}</span>
                  <button className="ab-btn-danger-sm" onClick={() => removeFromCart(id)} disabled={submitted}>✕</button>
                </div>
              ) : null;
            })
          }
          {cart.length > 0 && (
            <div className="ab-cart-total">Total: <strong>₹{cartTotal.toLocaleString()}</strong></div>
          )}
        </div>
      </div>

      <div className="ab-answer-row">
        <label className="ab-label">Total cart value:</label>
        <input value={answer} onChange={e => setAnswer(e.target.value)} className="ab-input ab-answer-input"
          placeholder="e.g. 19,498" disabled={submitted} />
        {!submitted
          ? <button className="ab-action-btn" onClick={handleSubmit}>Submit Answer</button>
          : <div className="ab-submitted-badge">✓ Submitted — Evaluating...</div>
        }
      </div>
    </div>
  );
}
